// src/components/UI/DateRangePicker.js
// Compact, smart date selector inspired by Stripe & Linear
import React, { useState, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
  Popover,
  Chip,
  TextField,
  Button,
  Divider,
  Stack,
  ClickAwayListener
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  CalendarMonth,
  Today,
  Close
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDateFilter } from '../../contexts/DateFilterContext';

const DateRangePicker = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  const { t } = useTranslation();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const {
    filterMode,
    selectedMonth,
    selectedYear,
    currentPeriodDisplay,
    isCurrentMonth,
    goToPreviousMonth,
    goToNextMonth,
    goToCurrentMonth,
    setPresetRange,
    setCustomRange,
    switchToMonthMode,
    effectiveStartDate,
    effectiveEndDate
  } = useDateFilter();

  const presets = [
    { id: 'last7days', label: '7D' },
    { id: 'last30days', label: '30D' },
    { id: 'last3months', label: '3M' },
    { id: 'thisYear', label: 'YTD' },
    { id: 'allTime', label: '∞' },
  ];

  const [activePreset, setActivePreset] = useState(null);

  const handlePreset = (id) => {
    setPresetRange(id);
    setActivePreset(id);
    setOpen(false);
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      const s = new Date(customStart); s.setHours(0,0,0,0);
      const e = new Date(customEnd); e.setHours(23,59,59,999);
      if (s > e) return;
      setCustomRange(s, e);
      setActivePreset('custom');
      setOpen(false);
    }
  };

  const handleMonthMode = () => {
    switchToMonthMode();
    setActivePreset(null);
    setOpen(false);
  };

  // Compact pill style for preset chips
  const chipSx = (isActive) => ({
    height: 28,
    fontSize: '0.7rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: `1px solid ${isActive 
      ? theme.palette.primary.main 
      : alpha(theme.palette.divider, 0.3)}`,
    bgcolor: isActive 
      ? alpha(theme.palette.primary.main, 0.15) 
      : 'transparent',
    color: isActive 
      ? theme.palette.primary.main 
      : theme.palette.text.secondary,
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.1),
      borderColor: theme.palette.primary.main,
    },
    transition: 'all 0.15s ease',
  });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        py: 0.75,
        px: 1,
        borderRadius: 2,
        background: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(12px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        boxShadow: `0 1px 3px ${alpha('#000', 0.06)}`,
        width: 'fit-content',
        maxWidth: '100%',
        mx: 'auto',
        flexWrap: 'nowrap',
        minHeight: 40,
      }}
    >
      {/* Left Arrow — always visible */}
      <IconButton
        onClick={() => { handleMonthMode(); goToPreviousMonth(); }}
        size="small"
        sx={{
          width: 28, height: 28,
          color: theme.palette.text.secondary,
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
        }}
      >
        <ChevronLeft sx={{ fontSize: 18 }} />
      </IconButton>

      {/* Center: Period Display — clickable to open popover */}
      <Box
        ref={anchorRef}
        onClick={() => setOpen(true)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: isMobile ? 1 : 1.5,
          py: 0.25,
          borderRadius: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.06),
          },
          transition: 'background 0.15s ease',
          minWidth: 0,
        }}
      >
        <CalendarMonth sx={{ fontSize: 16, color: theme.palette.primary.main, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: isMobile ? '0.75rem' : '0.82rem',
            fontWeight: 600,
            color: theme.palette.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: isMobile ? 130 : 200,
            letterSpacing: '-0.01em',
          }}
        >
          {currentPeriodDisplay}
        </Typography>
        {filterMode === 'range' && activePreset && (
          <Chip
            label={presets.find(p => p.id === activePreset)?.label || '⚙'}
            size="small"
            sx={{
              height: 18, fontSize: '0.6rem', fontWeight: 700,
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.main,
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
        )}
      </Box>

      {/* Right Arrow */}
      <IconButton
        onClick={() => { handleMonthMode(); goToNextMonth(); }}
        size="small"
        sx={{
          width: 28, height: 28,
          color: theme.palette.text.secondary,
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
        }}
      >
        <ChevronRight sx={{ fontSize: 18 }} />
      </IconButton>

      {/* Today button — only if not current month */}
      {!isCurrentMonth && filterMode === 'month' && (
        <IconButton
          onClick={goToCurrentMonth}
          size="small"
          title="Go to today"
          sx={{
            width: 28, height: 28,
            color: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
          }}
        >
          <Today sx={{ fontSize: 16 }} />
        </IconButton>
      )}

      {/* Range active indicator — clear button */}
      {filterMode === 'range' && (
        <IconButton
          onClick={handleMonthMode}
          size="small"
          title="Back to month view"
          sx={{
            width: 22, height: 22,
            color: theme.palette.error.main,
            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) },
          }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      {/* Popover — Quick presets + custom range */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2.5,
              minWidth: isMobile ? 260 : 300,
              maxWidth: 340,
              overflow: 'visible',
              background: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              boxShadow: `0 12px 40px ${alpha('#000', 0.12)}`,
            }
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Section: Quick Ranges */}
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: theme.palette.text.disabled, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
            {t('dateRange.quickRanges', 'Quick ranges')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
            {presets.map(p => (
              <Chip
                key={p.id}
                label={p.label}
                onClick={() => handlePreset(p.id)}
                variant="outlined"
                sx={chipSx(activePreset === p.id)}
              />
            ))}
          </Box>

          <Divider sx={{ my: 1.5, borderColor: alpha(theme.palette.divider, 0.08) }} />

          {/* Section: Custom Range */}
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: theme.palette.text.disabled, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
            {t('dateRange.custom', 'Custom range')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              size="small"
              InputProps={{ sx: { fontSize: '0.75rem', height: 34 } }}
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.disabled, px: 0.25 }}>→</Typography>
            <TextField
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              size="small"
              InputProps={{ sx: { fontSize: '0.75rem', height: 34 } }}
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Stack>
          <Button
            fullWidth
            size="small"
            variant="contained"
            onClick={handleCustomApply}
            disabled={!customStart || !customEnd}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.75rem',
              fontWeight: 600,
              py: 0.6,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            {t('dateRange.apply', 'Apply')}
          </Button>

          <Divider sx={{ my: 1.5, borderColor: alpha(theme.palette.divider, 0.08) }} />

          {/* Section: Back to Month */}
          <Button
            fullWidth
            size="small"
            variant="text"
            startIcon={<CalendarMonth sx={{ fontSize: 16 }} />}
            onClick={handleMonthMode}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.72rem',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              justifyContent: 'flex-start',
              py: 0.5,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
            }}
          >
            {t('dateRange.monthView', 'Month view')}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default DateRangePicker;
